import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { experimentalStyled as styled } from '@mui/material/styles';
import DashboardSidebar from './DashboardSidebar';
import DashboardNavbar from './DashboardNavbar';
import useAuth from 'src/hooks/useAuth';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { setExpandedContent } from 'src/redux/slices/Main/main';
import NewVersionValidation from 'src/components/NewVersionValidation';
import SessionExpiryAlert from 'src/components/SessionExpiryAlert';
import ImpersonatedUserBanner from 'src/UI/Banners/ImpersonatedUserBanner';
import HeaderTour from 'src/components/Header/HeaderTour';
import QSCalculationTour from 'src/components/QuestionnaireSummary/QSummary/QSCalculationTour';
import RentersLayout from 'src/layouts/RentersLayout';
import PendingVerificationsModal from 'src/components/Ulcm/PendingVerificationsModal';
import FloatingChatButton from 'src/components/ConnectChat/FloatingChatButton';
import FloatingBatchTray from 'src/components/Compliance/Lottery/Tray/FloatingBatchTray';
import CampaignTray from 'src/pages/Campaigns/Tray/CampaignTray';
// ----------------------------------------------------------------------

const MIN_SCREEN_WIDTH = 1280;
const RESIDENT = 'resident';

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  flexDirection: 'row',
  position: 'relative',
  // overflow: 'hidden',
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingBottom: theme.spacing(10),
  paddingRight: '30px !important',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: '30px !important',
  },
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { pathname } = useLocation();

  const isNewRentersExperienceEnabled = useSelector(
    (state) => state.featureFlags.flags.ENABLE_NEW_RENTER_EXPERIENCE,
    shallowEqual
  );
  const showNewExperience =
    isNewRentersExperienceEnabled && user?.sees_new_experience;

  const isLotteryEnabled = useSelector(
    (state) => Boolean(state.featureFlags?.flags?.ENABLE_LOTTERY),
    shallowEqual
  );

  const isCompliance = pathname.includes('/workbench');
  const showDashboard =
    (pathname.includes('/workbench/user') &&
      !pathname.includes('/workbench/user/settings')) ||
    pathname.includes('/summary');
  const isCertificationConfigWorkbench = pathname.match(
    '/certification-config/(.+)$'
  );
  const isCertificationConfigList = pathname.match(
    '/workbench/(.+)/certification-config(/*)$'
  );
  const isUserManagement = pathname.startsWith('/user-management');
  const isApplicantsPage =
    pathname.includes('/workbench/my-renters') ||
    pathname.includes('/workbench/team-renters') ||
    pathname.includes('/workbench/my-work') ||
    pathname.includes('/workbench/workgroups');

  const isBulkCommunications = pathname.includes(
    '/workbench/bulk_communications'
  );

  const isCreateQuestionnaire = pathname.includes('/create-questionnaire');

  const isPropertyPage =
    pathname.match('/workbench/(.+)/?$') &&
    !pathname.includes('/certification-config') &&
    !pathname.includes('/create-questionnaire') &&
    !pathname.includes('/my-renters') &&
    !pathname.includes('/team-renters') &&
    !pathname.includes('/my-work') &&
    !pathname.includes('/workgroups') &&
    !pathname.includes('/bulk_communications');

  const dispatch = useDispatch();

  const expandedPageContent = useSelector(
    ({ main: { expandedPageContent } }) => expandedPageContent,
    shallowEqual
  );

  const forceExpanded = useSelector(
    ({ main: { forceExpanded } }) => forceExpanded,
    shallowEqual
  );

  const parentIsLoading = useSelector(
    ({ calcBuilder: { parentIsLoading } }) => parentIsLoading,
    shallowEqual
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const mainWrapperSx = useMemo(() => {
    if (isCertificationConfigWorkbench) {
      return { paddingTop: '0px', paddingBottom: '10px' };
    }
    if (isCertificationConfigList) {
      return { paddingTop: '30px', paddingBottom: '10px' };
    }
    if (isUserManagement) {
      // UnifiedHeader flows inline (position:static) and must sit flush at
      // the top of the main area. HeaderIcons are position:fixed and float
      // over the header corner, so no top padding is needed.
      return { paddingTop: '0px' };
    }
    if (pathname === '/workbench/notifications') {
      return { paddingTop: '25px' };
    }
    if (pathname === '/workbench/user/settings') {
      return { paddingTop: '25px' };
    }
    if (pathname.includes('/workbench/user/communications')) {
      return { paddingTop: '25px' };
    }
    // v2: /:cert_id/household matches '/household' (no trailing slash for segment)
    if (pathname.includes('/workbench/') && pathname.includes('/household')) {
      return { paddingTop: '25px' };
    }
    if (pathname.includes('/workbench/') && pathname.includes('/units/')) {
      return { paddingTop: '25px' };
    }
    if (isCreateQuestionnaire) {
      return { paddingTop: '25px' };
    }
    if (isPropertyPage) {
      return { paddingTop: '25px' };
    }
    if (!isCompliance) {
      return { paddingTop: '25px' };
    }
    if (isApplicantsPage) {
      return { paddingTop: '25px', paddingBottom: '30px' };
    }
    if (isBulkCommunications) {
      return { paddingTop: '25px' };
    }
    if (showDashboard) {
      return { paddingTop: '30px' };
    }
    // For compliance pages with sticky headers, use appropriate padding
    if (isCompliance) {
      return { paddingTop: '160px' }; // Header height (140px) + some spacing (20px)
    }
    // check if location has file-manager-v2 either as param or url
    if (pathname.includes('file-manager-v2')) {
      return { paddingTop: '0', paddingBottom: '10px' };
    }
    return {};
  }, [
    isApplicantsPage,
    isCertificationConfigList,
    isCertificationConfigWorkbench,
    isCompliance,
    isBulkCommunications,
    isCreateQuestionnaire,
    isUserManagement,
    showDashboard,
    pathname,
    isPropertyPage,
  ]);

  useEffect(() => {
    const onResize = () => {
      if (forceExpanded) {
        return;
      }
      if (isUserManagement) {
        return;
      }
      if (window.innerWidth > MIN_SCREEN_WIDTH && !open) {
        setOpen(true);
        dispatch(setExpandedContent(false));
      }
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [dispatch, forceExpanded, open, isUserManagement]);

  /*   useEffect(() => {
   LogRocket.identify(user.id, {
   name: user.first_name + " " + user.last_name,
   email: user.email
   });
   }, [user]); */

  useEffect(() => {
    if (window.innerWidth < MIN_SCREEN_WIDTH) {
      return;
    }
    if (isUserManagement) {
      // User management tool has no relevant sidebar items — keep it
      // collapsed so the wide AG Grid has room to breathe.
      setOpen(false);
      return;
    }
    if (expandedPageContent) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [expandedPageContent, isUserManagement]);

  // Only show header tour for compliance users and not on /user paths
  const shouldShowHeaderTour = isCompliance && !pathname.includes('/user');

  if (showNewExperience) {
    return <RentersLayout />;
  }

  return (
    <>
      {shouldShowHeaderTour ? (
        <HeaderTour user={user}>
          <RootStyle>
            <QSCalculationTour tableIsLoading={parentIsLoading}>
              <DashboardSidebar
                isOpenSidebar={open}
                onCloseSidebar={() => setOpen(false)}
              />
              <NewVersionValidation />
              {user?.impersonator && <ImpersonatedUserBanner />}
              <DashboardNavbar
                onOpenSidebar={handleOpen}
                isResident={
                  !!user?.groups.find((g) => g?.name === RESIDENT) &&
                  !isCompliance
                }
              />
              <MainStyle id="testing-main" style={mainWrapperSx}>
                <Outlet id="test-outlet" />
                <SessionExpiryAlert />
                <PendingVerificationsModal />
              </MainStyle>
            </QSCalculationTour>
          </RootStyle>
        </HeaderTour>
      ) : (
        <RootStyle>
          <QSCalculationTour tableIsLoading={parentIsLoading}>
            <DashboardSidebar
              isOpenSidebar={open}
              onCloseSidebar={() => setOpen(false)}
            />
            <NewVersionValidation />
            {user?.impersonator && <ImpersonatedUserBanner />}
            <DashboardNavbar
              onOpenSidebar={handleOpen}
              isResident={
                !!user?.groups.find((g) => g?.name === RESIDENT) &&
                !isCompliance
              }
            />
            <MainStyle id="testing-main" style={mainWrapperSx}>
              <Outlet id="test-outlet" />
              <SessionExpiryAlert />
              <PendingVerificationsModal />
            </MainStyle>
          </QSCalculationTour>
        </RootStyle>
      )}
      {(user?.connectChatEnabled || user?.supportCasesEnabled) && (
        <FloatingChatButton />
      )}
      <FloatingBatchTray
        enabled={isLotteryEnabled && Boolean(user?.lotteryEligible)}
      />
      {/* ENG-4183 Campaigns tray. `campaigns_eligible` is the server's flag +
          permission + property-scope gate; the UI never infers it. */}
      <CampaignTray enabled={Boolean(user?.campaignsEligible)} />
    </>
  );
}
